<template>
    <div class="stage stage--full-size" v-if="visible" data-stage=4>

        <div class="contents detail-stage" v-if="opened">
            <div class="scroll-height">

                <booking-summary
                  :loading="loading"
                  :error="error"
                  :status="status || null"
                  :venue="booking.venue"
                  :pax="booking.pax"
                  :date="booking.date"
                  :type="booking.type"
                  :times="booking.times"
                  :preorder-payment-msg="preorderAndPaymentMsg || null"
                  v-on:changeDetails="changeDetails"
                  v-on:findAlternatives="findAlternatives">
                </booking-summary>

                <hr class="enquire-separator">

                <form>

                  <template v-if="!loading && !error">
                    <offers-input
                      v-if="bookingDetails.offers.length"
                      :offers="bookingDetails.offers"
                      v-model="$root.booking.offer">
                    </offers-input>

                    <package-input
                      v-if="bookingDetails.packages.length"
                      :packages="bookingDetails.packages"
                      v-model="$root.booking.package">
                    </package-input>

                    <user-details-form
                        v-if="!loading && status !== TimeAction.reject"
                        :booking-details="bookingDetails"
                        v-on:openModal="openModal"
                        v-on:bookingSent="bookingSent"
                        v-model="$root.booking.user"
                        ref="userForm">
                    </user-details-form>

                    <custom-field
                        v-if="bookingDetails.hasCustomField"
                        :label="bookingDetails.customFieldLabel"
                        v-model="$root.booking.customField">
                    </custom-field>

                    <tags-input
                      v-if="bookingDetails.hasTagFields"
                      :fields="bookingDetails.tagFields"
                      v-model="$root.booking.tags">
                    </tags-input>

                    <special-request
                      v-model="$root.booking.specialRequest">
                    </special-request>

                    <marketing-checkboxes
                      v-model="$root.booking.marketing"
                      :show-preferences="!loggedIn"
                      :show-dmn="!loggedIn && bookingDetails.shouldShowNewsletterSignup"
                      :partner-msg="bookingDetails.getCustomMarketingOptInMsg"
                      :preferences="bookingDetails.marketingPreferences">
                    </marketing-checkboxes>

                    <booking-policy
                      v-if="bookingDetails.hasBookingPolicy"
                      :policy="bookingDetails.bookingPolicy"
                      v-model="$root.booking.policyConfirm"
                      ref="policyCheckbox">
                    </booking-policy>

                    <submit-booking-button
                      v-on:click="submitForm"
                      :disabled="disableSubmitBtn"
                      :label="submitButtonTxt">
                    </submit-booking-button>

                  </template>

                  <template v-if="!loading && error">
                    <p>An error occurred when checking availability for this booking.</p>
                  </template>
                </form>
            </div>
        </div>
    </div>
</template>

<script>
  import { TimeAction } from '../services/TimesProvider'
  import BookingDetailsProvider, { TagType } from '../services/BookingDetailsProvider'
  import bookingPolicy from './bookingPolicy'
  import bookingSummary from './bookingSummary'
  import customField from './customField'
  import DateService from '../services/DateService'
  import marketingCheckboxes from './marketingCheckboxes'
  import offersInput from './offersInput'
  import packageInput from './packageInput'
  import specialRequest from './specialRequest'
  import submitBookingButton from './submitBookingButton'
  import SubmitBookingProvider from '../services/SubmitBookingProvider'
  import tagsInput from './tagsInput'
  import userDetailsForm from './userDetailsForm'

  export default {
    name: 'detailsStage',
    props: [
      'booking',
      'visible',
      'opened'
    ],
    components: {
      bookingPolicy,
      bookingSummary,
      customField,
      marketingCheckboxes,
      offersInput,
      packageInput,
      specialRequest,
      submitBookingButton,
      tagsInput,
      userDetailsForm
    },
    data () {
      return {
        TimeAction,
        DateService,
        error: false,
        loading: true,
        bookingDetails: null,
        loggedIn: false,
        disableSubmitBtn: false
      }
    },
    mounted () {
      const urlOptions = {
        source: this.$root.dmnOptions.from,
        affiliateId: this.$root.dmnOptions.affiliateId,
        customSource: this.$root.dmnOptions.customSource,
        returnUrl: this.$root.dmnOptions.returnUrl,
        googleAnalyticsCode: this.$root.dmnOptions.googleAnalyticsCode,
        googleTagManagerCode: this.$root.dmnOptions.googleTagManagerCode
      }

      this.submitBookingProvider = new SubmitBookingProvider(this.$root.booking, urlOptions)
    },
    methods: {
      setUpTags () {
        this.$root.booking.tagFields = this.bookingDetails.tagFields

        this.bookingDetails.tagFields.forEach((tag) => {
          switch (tag.type) {
            case TagType.multiselect:
            case TagType.select:
              this.$root.booking.tags[tag.name] = []
              break
            case TagType.boolean:
              this.$root.booking.tags[tag.name] = false
              break
            default:
              this.$root.booking.tags[tag.name] = ''
          }
        })
      },

      getBookingDetails () {
        this.error = false

        this.bookingDetailsProvider.requestBookingDetails()
          .then((bookingDetails) => {
            this.bookingDetails = bookingDetails
            this.setUpTags()
          })
          .catch((err) => {
            if (err === 'booking_unavailable') {
              this.error = true
            }
          })
          .then(() => {
            this.loading = false
          })
      },
      scrollToFirstError () {
        const firstError = this.$el.getElementsByClassName('input__validation-error-msg')

        firstError[0].parentElement.scrollIntoView()
      },
      submitForm () {
        if (this.disableSubmitBtn) return

        if (!this.passesValidation()) {
          this.$nextTick(() => {
            this.scrollToFirstError()
          })

          return
        }

        if (this.bookingDetails.shouldCompleteInModal) {
          this.$root.booking.setContinueParameter(this.bookingDetails)
          return this.$emit('openModal')
        }

        this.disableSubmitBtn = true

        const booking = this.$root.booking
        const token = this.bookingDetails.accessToken

        this.submitBookingProvider.submitBooking(booking, token)
          .then((confirmedBooking) => {
            this.disableSubmitBtn = false
            this.$emit('bookingSent', confirmedBooking)
          })
      },
      passesValidation () {
        const userValidation = this.$refs.userForm.passesValidation()

        if (this.$refs.policyCheckbox) {
          return this.$refs.policyCheckbox.passesValidation() && userValidation
        }

        return userValidation
      },
      changeDetails () {
        this.$emit('changeDetails')
      },
      findAlternatives () {
        this.$emit('findAlternatives')
      },
      bookingSent (confirmedBooking) {
        this.$emit('bookingSent', confirmedBooking)
      },
      reinitialiseProvider () {
        this.bookingDetails = null
        this.loading = true

        this.bookingDetailsProvider = new BookingDetailsProvider(
          this.booking, this.$root.dmnOptions.from, this.$root.dmnOptions.affiliateId
        )
      },
      requestBookingDetails () {
        this.reinitialiseProvider()
        this.getBookingDetails()
      },
      openModal () {
        this.$emit('openModal')
      }
    },
    computed: {
      shouldShowForm: function () {
        return !this.loading && this.bookingDetails.status !== TimeAction.reject
      },
      submitButtonTxt: function () {
        switch (true) {
          case this.disableSubmitBtn:
            return ''
          case this.bookingDetails.shouldCompleteInModal:
            return 'Continue...'
          case this.bookingDetails.shouldConfirm:
            return 'Book Now'
          default:
            return 'Enquire Now'
        }
      },
      status: function () {
        return this.loading || this.error ? null : this.bookingDetails.status
      },
      preorderAndPaymentMsg: function () {
        return this.loading || this.error ? null : this.bookingDetails.preorderAndPaymentMsg
      }
    }
  }
</script>



// WEBPACK FOOTER //
// detailsStage.vue?50b33e40